// UTM + paid-click attribution capture utility.
//
// Reads attribution from the URL on every visit, persists it in
// localStorage, and exposes it to the analytics event enrichers so paid
// Facebook / Google / Bing traffic can be attributed end-to-end.
//
// Storage uses TWO independent localStorage keys (per the P0 spec):
//   - jingle_first_touch_attribution: set once on first ever visit
//   - jingle_latest_touch_attribution: overwritten whenever a new ad
//     click lands
//
// `first_touch` is what marketers want for ad attribution (which ad
// introduced the user). `latest_touch` is the most recent ad-click
// attribution and is useful for multi-click cycles.
//
// Backward-compat: an older single-key shape ("jingle.utmCapture")
// was used briefly. If present on first read, it is migrated to the
// two-key layout and the legacy key is removed.

const FIRST_TOUCH_KEY = "jingle_first_touch_attribution";
const LATEST_TOUCH_KEY = "jingle_latest_touch_attribution";
const LEGACY_NESTED_KEY = "jingle.utmCapture";

// Test-mode flag. Setting localStorage["jingle.testMode"] = "1" causes
// every analytics event to carry `is_test_account: true` and
// `is_test_traffic: true` so internal QA traffic can be filtered out
// of paid-ads reports. Toggle from DevTools — no UI.
const TEST_MODE_KEY = "jingle.testMode";

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

interface TouchData extends UtmParams {
  fbclid?: string;
  gclid?: string;
  msclkid?: string;
  landing_page?: string;
  referrer?: string;
  captured_at?: string;
}

function readFromUrl(): TouchData {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const result: TouchData = {};
  const source = params.get("utm_source");
  const medium = params.get("utm_medium");
  const campaign = params.get("utm_campaign");
  const content = params.get("utm_content");
  const term = params.get("utm_term");
  const fbclid = params.get("fbclid");
  const gclid = params.get("gclid");
  const msclkid = params.get("msclkid");
  if (source) result.utm_source = source;
  if (medium) result.utm_medium = medium;
  if (campaign) result.utm_campaign = campaign;
  if (content) result.utm_content = content;
  if (term) result.utm_term = term;
  if (fbclid) result.fbclid = fbclid;
  if (gclid) result.gclid = gclid;
  if (msclkid) result.msclkid = msclkid;
  return result;
}

// Returns just the hostname of document.referrer, or empty string if
// unavailable / same-origin. Full referrer URLs may contain sensitive
// query params from other sites — hostname is the useful signal.
function readReferrerHost(): string {
  if (typeof document === "undefined") return "";
  const ref = document.referrer;
  if (!ref) return "";
  try {
    const u = new URL(ref);
    if (typeof window !== "undefined" && u.host === window.location.host) {
      return ""; // same-origin nav, not a real referrer
    }
    return u.host;
  } catch {
    return "";
  }
}

function readLandingPath(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}

function readKey(key: string): TouchData | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as TouchData;
  } catch {
    return null;
  }
}

function writeKey(key: string, value: TouchData): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable in private mode — ignore.
  }
}

function removeKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// Migrate the brief single-nested-key shape ("jingle.utmCapture") that
// shipped earlier in this session into the two-key shape called for by
// the P0 spec. Idempotent: runs once and removes the legacy key.
function migrateLegacyIfNeeded(): void {
  if (typeof window === "undefined") return;
  if (readKey(FIRST_TOUCH_KEY)) return; // already on new shape
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LEGACY_NESTED_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed && typeof parsed === "object") {
      const first = (parsed.first_touch as TouchData | undefined) ?? null;
      const latest = (parsed.latest_touch as TouchData | undefined) ?? null;
      if (first) writeKey(FIRST_TOUCH_KEY, first);
      else writeKey(FIRST_TOUCH_KEY, parsed as TouchData); // flat fallback
      if (latest) writeKey(LATEST_TOUCH_KEY, latest);
      else writeKey(LATEST_TOUCH_KEY, parsed as TouchData);
    }
  } catch {
    /* unreadable legacy blob — ignore */
  }
  removeKey(LEGACY_NESTED_KEY);
}

function hasAttribution(t: TouchData): boolean {
  return Boolean(
    t.utm_source ||
      t.utm_medium ||
      t.utm_campaign ||
      t.utm_content ||
      t.utm_term ||
      t.fbclid ||
      t.gclid ||
      t.msclkid,
  );
}

// Capture attribution. Safe to call on every route change — first_touch
// is written exactly once (preserving the very first landing context),
// and latest_touch is updated whenever the URL carries new ad-click
// parameters.
export function captureUtmParams(): void {
  if (typeof window === "undefined") return;
  migrateLegacyIfNeeded();

  const urlTouch = readFromUrl();
  const now = new Date().toISOString();
  const landing = readLandingPath();
  const referrer = readReferrerHost();

  const first = readKey(FIRST_TOUCH_KEY);
  if (!first) {
    // First-ever visit — always record landing + referrer so even direct
    // traffic gets attributed as "direct" instead of being invisible.
    const seed: TouchData = {
      ...urlTouch,
      landing_page: landing,
      referrer,
      captured_at: now,
    };
    writeKey(FIRST_TOUCH_KEY, seed);
    writeKey(LATEST_TOUCH_KEY, seed);
    return;
  }

  // Returning visit — update latest_touch only when this pageload has
  // new ad-click params. Plain SPA navigation does not overwrite.
  if (hasAttribution(urlTouch)) {
    writeKey(LATEST_TOUCH_KEY, {
      ...urlTouch,
      landing_page: landing,
      referrer,
      captured_at: now,
    });
  }
}

// Legacy accessor: returns just the UTM fields from first_touch.
// Preserved for backward compatibility with existing callers
// (LaunchUpdatesForm submits these to the backend lead endpoint).
export function getUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};
  migrateLegacyIfNeeded();
  const first = readKey(FIRST_TOUCH_KEY);
  if (first) {
    const out: UtmParams = {};
    if (first.utm_source) out.utm_source = first.utm_source;
    if (first.utm_medium) out.utm_medium = first.utm_medium;
    if (first.utm_campaign) out.utm_campaign = first.utm_campaign;
    if (first.utm_content) out.utm_content = first.utm_content;
    if (first.utm_term) out.utm_term = first.utm_term;
    return out;
  }
  // No storage yet — fall back to URL so a form submitted on the very
  // first pageload (before captureUtmParams ran) still carries UTMs.
  const url = readFromUrl();
  const out: UtmParams = {};
  if (url.utm_source) out.utm_source = url.utm_source;
  if (url.utm_medium) out.utm_medium = url.utm_medium;
  if (url.utm_campaign) out.utm_campaign = url.utm_campaign;
  if (url.utm_content) out.utm_content = url.utm_content;
  if (url.utm_term) out.utm_term = url.utm_term;
  return out;
}

// Returns the test-mode flags merged into every analytics event when
// the visitor has manually enabled test mode in DevTools:
//
//   localStorage.setItem("jingle.testMode", "1")
//
// When enabled, every event carries `is_test_account: true` and
// `is_test_traffic: true` so paid-ads dashboards can filter internal
// QA traffic out of conversion reports. When disabled (the default),
// returns {} — sparse payload, matching the existing analytics style
// where absent values are omitted rather than sent as `false`. A
// GA4/Meta segment of "is_test_account != true" treats omitted keys
// and explicit `false` identically, so the sparse form is safe and
// minimizes payload weight on real visitors.
//
// Never throws. Safe in SSR (window-guarded) and in browsers where
// localStorage is unavailable.
export function getTestModeFlags(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    if (localStorage.getItem(TEST_MODE_KEY) === "1") {
      return { is_test_account: true, is_test_traffic: true };
    }
  } catch {
    /* localStorage unavailable — treat as production traffic */
  }
  return {};
}

// Returns the attribution payload merged into every analytics event
// (GA4 + Meta Pixel). Base utm_* fields come from first_touch (the
// most stable attribution signal); explicit first_touch_*/latest_touch_*
// triplets are added per spec so the data warehouse can analyze
// multi-click cycles without parsing full storage. Click IDs are sent
// as booleans (has_fbclid, has_gclid, has_msclkid) to keep payloads
// clean; the full IDs stay in localStorage for any future backend
// submission. Returns {} when no attribution is known so callers can
// safely spread the result.
export function getAttributionPayload(): Record<string, string | boolean> {
  if (typeof window === "undefined") return {};
  migrateLegacyIfNeeded();
  const first = readKey(FIRST_TOUCH_KEY);
  if (!first) return {};
  const latest = readKey(LATEST_TOUCH_KEY) ?? first;
  const out: Record<string, string | boolean> = {};

  // Base utm_* + click-ID booleans + landing + referrer (from first_touch)
  if (first.utm_source) out.utm_source = first.utm_source;
  if (first.utm_medium) out.utm_medium = first.utm_medium;
  if (first.utm_campaign) out.utm_campaign = first.utm_campaign;
  if (first.utm_content) out.utm_content = first.utm_content;
  if (first.utm_term) out.utm_term = first.utm_term;
  if (first.fbclid) out.has_fbclid = true;
  if (first.gclid) out.has_gclid = true;
  if (first.msclkid) out.has_msclkid = true;
  if (first.landing_page) out.landing_page = first.landing_page;
  if (first.referrer) out.referrer = first.referrer;

  // Explicit first_touch_* triplet
  if (first.utm_source) out.first_touch_source = first.utm_source;
  if (first.utm_medium) out.first_touch_medium = first.utm_medium;
  if (first.utm_campaign) out.first_touch_campaign = first.utm_campaign;

  // Explicit latest_touch_* triplet
  if (latest.utm_source) out.latest_touch_source = latest.utm_source;
  if (latest.utm_medium) out.latest_touch_medium = latest.utm_medium;
  if (latest.utm_campaign) out.latest_touch_campaign = latest.utm_campaign;

  return out;
}
